import {
  ANONYMOUS_USER_ID_KEY,
  DEBUG_MODE_KEY,
} from '@/constants/localStorage';
import { auth0Client } from '@/auth/Auth0Client';
import { getLayoverLogger } from '@/logging/LayoverLogger';

import { context, propagation } from '@opentelemetry/api';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
} as const;

export class BaseApi {
  protected _anonymousUserId;
  static _debugMode?: boolean;

  constructor() {
    const storedAnonymousUserId = window.localStorage.getItem(
      ANONYMOUS_USER_ID_KEY,
    );

    if (storedAnonymousUserId != null) {
      this._anonymousUserId = storedAnonymousUserId;
    } else {
      this._anonymousUserId = uuidv4();
      window.localStorage.setItem(ANONYMOUS_USER_ID_KEY, this._anonymousUserId);
    }
  }

  static setDebugMode(debugMode: string): void {
    localStorage.setItem(DEBUG_MODE_KEY, debugMode);
    this._debugMode = debugMode === 'true';
  }

  static get getDebugMode(): boolean | undefined {
    if (this._debugMode === undefined) {
      this._debugMode = localStorage.getItem(DEBUG_MODE_KEY) === 'true';
    }
    return this._debugMode;
  }

  resetAnonymousUserId(): void {
    this._anonymousUserId = uuidv4();
    window.localStorage.setItem(ANONYMOUS_USER_ID_KEY, this._anonymousUserId);
  }

  getAnonymousUserId(): string {
    return this._anonymousUserId;
  }

  protected createDefaultHeaders = async () => {
    const token = await auth0Client.getToken().catch((error: unknown) => {
      console.error('Error getting token: ', error);
      return undefined;
    });

    return {
      ...DEFAULT_HEADERS,
      ...(token
        ? { Authorization: `Bearer ${token}` }
        : { 'X-Anonymous-User-ID': this._anonymousUserId }),
    };
  };

  async request<T>({
    url,
    method,
    headers = {},
    query = {},
    body = '',
    shouldThrowOnServerError = true,
    ...options
  }: {
    url: string;
    method: string;
    headers?: Record<string, string>;
    query?: Record<string, string>;
    body?: any;
    shouldThrowOnServerError?: boolean;
    options?: RequestInit;
  }): Promise<{
    response: Response;
    data: T;
  }> {
    let bodyStr: null | string = null;
    switch (method.toUpperCase()) {
      case 'GET':
      case 'HEAD':
        break;
      default:
        bodyStr = typeof body === 'string' ? body : JSON.stringify(body || {});
        break;
    }

    // Inject the trace context from the passed span into the request headers so the API can continue the trace
    const traceHeaders: Record<string, string> = {};
    propagation.inject(context.active(), traceHeaders);

    const init: Record<string, any> = {
      method,
      headers: {
        ...(await this.createDefaultHeaders()),
        ...headers,
        ...traceHeaders,
      },
      body: bodyStr,
      ...options,
    };

    const debugMode = new URLSearchParams(window.location.search).get('debug');
    if (debugMode) {
      BaseApi.setDebugMode(debugMode);
    }
    if (BaseApi.getDebugMode) {
      query.debug = BaseApi.getDebugMode.toString();
    }

    if (Object.keys(query).length > 0) {
      url += '?' + new URLSearchParams(query).toString();
    }

    const resp = await fetch(url, init);
    if (!resp.ok) {
      const extraProps = {
        url: url,
        response: resp,
      };
      getLayoverLogger().logError(
        `Request failed with status ${resp.status}`,
        extraProps,
      );
      if (shouldThrowOnServerError) {
        throw new Error(`Request failed with status ${resp.status}`);
      }
    }
    const data = await resp.json();
    return {
      response: resp,
      data,
    };
  }
}
